/**
 * Accessibility Fixes - Automatic Alt Attribute Addition
 * Ensures all images have proper alt attributes for better accessibility
 * Particularly useful for third-party widgets and dynamically loaded content
 */

(function() {
  'use strict';

  /**
   * Default alt text mappings based on image source patterns
   */
  const ALT_MAPPINGS = {
    'whatsapp': 'WhatsApp Icon',
    'facebook': 'Facebook Icon',
    'instagram': 'Instagram Icon',
    'twitter': 'Twitter Icon',
    'youtube': 'YouTube Icon',
    'linkedin': 'LinkedIn Icon',
    'pinterest': 'Pinterest Icon',
    'trustpilot': 'Trustpilot Review Badge',
    'google': 'Google Review Badge',
    'ssl': 'SSL Security Badge',
    'secure': 'Security Badge',
    'payment': 'Payment Method Icon',
    'visa': 'Visa Payment Icon',
    'mastercard': 'Mastercard Payment Icon',
    'paypal': 'PayPal Payment Icon',
    'logo': 'Company Logo',
    'brand': 'Brand Logo'
  };

  /**
   * Determines appropriate alt text for an image
   * @param {HTMLImageElement} img - The image element
   * @returns {string} Appropriate alt text
   */
  function getAltText(img) {
    const src = img.src.toLowerCase();
    const className = (img.className || '').toLowerCase();
    
    // Check for specific patterns in src or class names
    for (const [key, altText] of Object.entries(ALT_MAPPINGS)) {
      if (src.includes(key) || className.includes(key)) {
        return altText;
      }
    }
    
    // Check parent elements for context
    let parent = img.parentElement;
    let depth = 0;
    while (parent && depth < 3) {
      const parentClass = (parent.className || '').toLowerCase();
      const parentId = (parent.id || '').toLowerCase();
      
      for (const [key, altText] of Object.entries(ALT_MAPPINGS)) {
        if (parentClass.includes(key) || parentId.includes(key)) {
          return altText;
        }
      }
      
      parent = parent.parentElement;
      depth++;
    }
    
    // Fallback based on image dimensions and position
    if (img.width <= 50 && img.height <= 50) {
      return 'Icon';
    }
    
    return 'Decorative image';
  }

  /**
   * Adds alt attributes to images that don't have them
   * @param {NodeList|HTMLElement} container - Container to search in
   */
  function fixImageAltAttributes(container = document) {
    const images = container.querySelectorAll ? 
      container.querySelectorAll('img:not([alt])') : 
      [container].filter(el => el.tagName === 'IMG' && !el.hasAttribute('alt'));
    
    images.forEach(img => {
      const altText = getAltText(img);
      img.setAttribute('alt', altText);
      
      // Log for debugging in development
      if (window.Shopify && window.Shopify.designMode) {
        console.log(`Added alt attribute "${altText}" to image:`, img.src);
      }
    });
  }

  /**
   * Observer to watch for dynamically added content
   */
  function createContentObserver() {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is an image
              if (node.tagName === 'IMG') {
                fixImageAltAttributes(node);
              }
              // Check if the added node is a link
              else if (node.tagName === 'A') {
                // Single link node
                if (node.href && (node.href.includes('javascript:') || node.href.endsWith('#'))) {
                  setTimeout(() => fixUncrawlableLinks(), 50);
                }
                setTimeout(() => fixLinkAccessibility(), 50);
              }
              // Check for content within the added node
              else if (node.querySelector) {
                fixImageAltAttributes(node);
                
                // Check for uncrawlable links within the node
                const uncrawlableLinks = node.querySelectorAll('a[href="javascript:void(0)"], a[href^="javascript:"], a[href="#"]');
                if (uncrawlableLinks.length > 0) {
                  setTimeout(() => fixUncrawlableLinks(), 50);
                }
                
                // Check for links without accessible names
                const linksWithoutLabels = node.querySelectorAll('a:not([aria-label]):not([title])');
                if (linksWithoutLabels.length > 0) {
                  setTimeout(() => fixLinkAccessibility(), 50);
                }
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Fixes links without discernible names
   */
  function fixLinkAccessibility() {
    const links = document.querySelectorAll('a:not([aria-label]):not([title])');
    
    links.forEach(link => {
      // Skip links that already have text content or screen reader text
      if (link.innerText.trim() || link.querySelector('.sr-only, .visually-hidden')) {
        return;
      }
      
      // Check if link contains only images
      const images = link.querySelectorAll('img');
      if (images.length > 0 && !link.innerText.trim()) {
        // Use the first image's alt text or create a descriptive label
        const firstImg = images[0];
        const altText = firstImg.alt || firstImg.title || 'Link';
        link.setAttribute('aria-label', altText);
        
        // Log for debugging in development
        if (window.Shopify && window.Shopify.designMode) {
          console.log(`Added aria-label "${altText}" to link:`, link.href);
        }
      }
      
      // Check if link is in promotional/scrolling sections
      if (link.closest('.m-scrolling-promotion, .m-promotion')) {
        const parentItem = link.closest('.m-promotion__item');
        if (parentItem) {
          const text = parentItem.querySelector('.m-promotion__text');
          if (text && text.innerText.trim()) {
            link.setAttribute('aria-label', text.innerText.trim());
          } else if (!link.hasAttribute('aria-label')) {
            link.setAttribute('aria-label', 'Promotional link');
          }
        }
      }
    });
  }

  /**
   * Fixes uncrawlable JavaScript links for better SEO
   */
  function fixUncrawlableLinks() {
    // Find links with javascript:void(0) or similar patterns
    const uncrawlableLinks = document.querySelectorAll('a[href="javascript:void(0)"], a[href^="javascript:"], a[href="#"]');
    
    uncrawlableLinks.forEach(link => {
      const parent = link.closest('[data-product-id], [data-product], .product-card, .m-product-card');
      const isReviewLink = link.closest('.review, .reviews, [class*="review"], [id*="review"]');
      
      // Skip if link already has proper functionality or content
      if (link.getAttribute('data-skip-seo-fix') || 
          link.querySelector('form, input, button')) {
        return;
      }
      
      let newHref = '#';
      
      // For review-related links
      if (isReviewLink) {
        if (parent && parent.dataset.productId) {
          newHref = `/products/${parent.dataset.productId}#reviews`;
        } else if (parent && parent.querySelector('[data-product-id]')) {
          const productId = parent.querySelector('[data-product-id]').dataset.productId;
          newHref = `/products/${productId}#reviews`;
        } else {
          // Try to find product URL from current page context
          const productUrl = window.location.pathname;
          if (productUrl.includes('/products/')) {
            newHref = productUrl + '#reviews';
          } else {
            newHref = '#reviews';
          }
        }
      }
      
      // For product-related links
      else if (parent) {
        const productLink = parent.querySelector('a[href*="/products/"]:not([href^="javascript:"])');
        if (productLink) {
          newHref = productLink.href;
        }
      }
      
      // Update the href while preserving existing functionality
      link.href = newHref;
      
      // Preserve original JavaScript functionality if it exists
      const originalOnclick = link.getAttribute('onclick');
      if (originalOnclick && !originalOnclick.includes('return false')) {
        link.setAttribute('onclick', originalOnclick + '; return true;');
      }
      
      // Log for debugging in development
      if (window.Shopify && window.Shopify.designMode) {
        console.log(`Fixed uncrawlable link from javascript:void(0) to: ${newHref}`);
      }
    });
  }

  /**
   * Initialize accessibility fixes
   */
  function init() {
    // Fix existing images immediately
    fixImageAltAttributes();
    
    // Fix link accessibility
    fixLinkAccessibility();
    
    // Fix uncrawlable links
    fixUncrawlableLinks();
    
    // Set up observer for dynamically loaded content
    createContentObserver();
    
    // Re-check after common loading events
    const recheckEvents = ['DOMContentLoaded', 'load', 'page:loaded'];
    recheckEvents.forEach(event => {
      window.addEventListener(event, () => {
        setTimeout(() => {
          fixImageAltAttributes();
          fixUncrawlableLinks();
          fixLinkAccessibility();
        }, 100);
      });
    });
    
    // Additional check for review apps that load asynchronously
    const checkReviewApps = () => {
      // Common review app selectors
      const reviewSelectors = [
        '.yotpo', '.loox-reviews', '.stamped-reviews', '.judgeme-reviews',
        '.growave-reviews', '.socialshopwave', '.automizely-reviews',
        '[id*="review"]', '[class*="review"]', '.spr-reviews'
      ];
      
      reviewSelectors.forEach(selector => {
        const reviewWidgets = document.querySelectorAll(selector);
        reviewWidgets.forEach(widget => {
          fixUncrawlableLinks();
          fixLinkAccessibility();
        });
      });
    };
    
    // Check for review apps periodically in case they load later
    setTimeout(checkReviewApps, 2000);
    setTimeout(checkReviewApps, 5000);
    
    // Special handling for common third-party widgets
    const checkThirdPartyWidgets = () => {
      // Check for common chat widgets
      const chatWidgets = document.querySelectorAll('.chat-widget, .messenger-widget, .support-widget');
      chatWidgets.forEach(widget => {
        setTimeout(() => fixImageAltAttributes(widget), 500);
      });
    };

    // Check for widgets after a delay to allow them to load
    setTimeout(checkThirdPartyWidgets, 1000);
    setTimeout(checkThirdPartyWidgets, 3000);
  }

  /**
   * Makes the mobile menu trigger usable from the keyboard.
   * The theme binds its click handler to the inner `.m-hamburger-box`, so Enter/Space
   * on the labelled `role="button"` wrapper is forwarded to it. aria-expanded is mirrored
   * from the drawer's real state rather than toggled, so it stays correct however the
   * drawer was opened or closed (trigger, close button, overlay, Escape).
   */
  function enableMenuButtonKeyboard() {
    const SELECTOR = '.m-menu-button[role="button"]';

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

      const trigger = event.target.closest(SELECTOR);
      if (!trigger) return;

      event.preventDefault();
      (trigger.querySelector('.m-hamburger-box') || trigger).click();
    });

    const drawer = document.querySelector('#m-menu-drawer');
    if (!drawer) return;

    const syncExpanded = function () {
      const trigger = document.querySelector(SELECTOR);
      if (!trigger) return;
      const isOpen = getComputedStyle(drawer).visibility !== 'hidden';
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    };

    new MutationObserver(syncExpanded).observe(drawer, {
      attributes: true,
      attributeFilter: ['class', 'style']
    });
    syncExpanded();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', enableMenuButtonKeyboard);
  } else {
    init();
    enableMenuButtonKeyboard();
  }

})();
